import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-scroll';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const Shows = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Placeholder data - you'll add real shows
  const shows = [
    { 
      date: 'Dec 14, 2025', 
      time: '5:00 PM',
      venue: 'Mercury Lounge', 
      location: 'New York, NY', 
      ticketLink: 'http://ticketmaster.com/the-band-solstice-w-boxrooster-new-york-new-york-12-14-2025/event/00006336BB9E3166' 
    },
    // Add more shows here
  ];

  return (
    <Box
      id="shows"
      ref={ref}
      sx={{
        minHeight: '100vh',
        py: 10,
        backgroundColor: '#121212',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '4rem' },
              color: '#FFFFFF',
            }}
          >
            SHOWS
          </Typography>
          <Box
            sx={{
              width: '100px',
              height: '4px',
              backgroundColor: theme.palette.primary.main,
              margin: '0 auto 6rem',
            }}
          />
        </motion.div>

        {shows.length > 0 ? (
          <Grid container spacing={3}>
            {shows.map((show, index) => (
              <Grid item xs={12} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      backgroundColor: '#1a1a1a',
                      border: `2px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        backgroundColor: '#222',
                        transform: 'translateX(10px)',
                      },
                      transition: 'all 0.3s',
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
                            <Box>
                              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                                {show.date}
                              </Typography>
                              {show.time && (
                                <Typography sx={{ color: '#AAA', fontSize: '0.9rem' }}>
                                  {show.time}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {show.venue}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <LocationOnIcon sx={{ color: theme.palette.primary.main, fontSize: '1rem' }} />
                            <Typography sx={{ color: '#AAA' }}>
                              {show.location}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <Button
                            variant="contained"
                            href={show.ticketLink}
                            target="_blank"
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: theme.palette.primary.light,
                              },
                            }}
                          >
                            GET TICKETS
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                backgroundColor: '#1a1a1a',
                borderRadius: 2,
                border: `2px dashed ${theme.palette.primary.main}`,
              }}
            >
              <Typography variant="h5" sx={{ color: '#FFFFFF', mb: 2 }}>
                No Upcoming Shows
              </Typography>
              <Typography sx={{ color: '#AAA', mb: 4 }}>
                Stay tuned! Sign up for our newsletter to be the first to know when we announce new dates.
              </Typography>
              <Button
                variant="contained"
                href="#contact"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                }}
              >
                SUBSCRIBE TO NEWSLETTER
              </Button>
            </Box>
          </motion.div>
        )}

        {/* Subscribe Box - Always shown */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          style={{ marginTop: shows.length > 0 ? '4rem' : '2rem' }}
        >
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              px: 4,
              backgroundColor: '#1a1a1a',
              borderRadius: 2,
              border: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            <Typography variant="h5" sx={{ color: '#FFFFFF', mb: 2, fontWeight: 'bold' }}>
              Stay Updated
            </Typography>
            <Typography sx={{ color: '#AAA', mb: 3 }}>
              Subscribe to hear about upcoming shows!
            </Typography>
            <Link to="contact" spy={true} smooth={true} offset={-70} duration={800} style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                SUBSCRIBE
              </Button>
            </Link>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Shows;
